// @flow
import type { Action } from "redux-action";
import actionCreators from "@modules/profile/actionCreator";
import http from "@http";
import { toast } from "react-toastify";
import {
  GetMyAbsences,
  GetMyAbsencesSchema,
  GetMyAbsence,
  GetUserActiveShiftPattern,
  GetMyClassificationsSchema,
  CreateMyAbsence,
  SetAbsenceStatus,
  GetMyStandardQuestions,
  AddMyStandardQuestions,
  GetMyDynamicQuestions,
  AddMyDynamicQuestions,
  GetMyReferralQuestions,
  AddMyReferralQuestions,
  AddMyAdditionalInfoQuestion,
  GetMyAbsenceDaysLost,
  UpdateMyAbsence,
  ExportMyAbsences,
  ExportMyAbsencesSuccess,
  GetCanOpenAbsence,
} from "@modules/profile/constants/actions";
import { all, call, put, select, takeEvery } from "redux-saga/effects";
import { parseSchema } from "@utils/schema";
import { sendSuccessMessage } from "@modules/app/actionCreator";
import { sagaGetAbsencesFilters } from "@modules/shared/selectors";
import { getUserId } from "@modules/account/selectors";
import { OPERATORS, transformFilters } from "@transformers/request";
import { downloadCsvFile } from "@sagas/utils";
import moment from "moment";

type MyAbsencesRequest = $Exact<{
  clientId: number,
  employeeId: number,
  sort?: string,
}>;

export function* onGetMyAbsences({
  payload,
}: Action<{ ...MyAbsencesRequest, page?: number }>): Generator<*, void, Object> {
  if (!payload || !payload.clientId || !payload.employeeId) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  const filters = yield select(sagaGetAbsencesFilters);
  const transformedFilters = transformFilters(filters, {
    starts_at: OPERATORS.GREATER_THAN,
    rtw_at: OPERATORS.LESS_THAN,
  });

  try {
    const response = yield call(
      http.get,
      `/api/v1/user/self-service/client/${payload.clientId}/absence`,
      {
        params: {
          search: transformedFilters,
          page: payload.page || 1,
          sort: "created_at,desc",
        },
      },
    );

    yield all([
      put(actionCreators.GetCanOpenAbsence({ clientId: payload.clientId })),
      put(actionCreators.GetMyAbsencesSuccess(response.data)),
    ]);
  } catch (e) {
    yield put(actionCreators.GetMyAbsencesError(e.response));
  }
}

export function* onGetMyAbsencesSchema({
  payload,
}: Action<{ clientId: number }>): Generator<*, void, Object> {
  if (!payload || !payload.clientId) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const schema = yield parseSchema("MyAbsenceDTO", { clientId: payload.clientId });

    yield put(actionCreators.GetMyAbsencesSchemaSuccess(schema));
  } catch (e) {
    yield put(actionCreators.GetMyAbsencesSchemaError(e.response));
  }
}

export function* onGetMyClassificationsSchema({
  payload,
}: Action<{ clientId: number, absenceReasonType: string }>): Generator<*, void, Object> {
  if (!payload || !payload.clientId || !payload.absenceReasonType) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const schema = yield parseSchema("MyAbsenceClassificationsDTO", {
      clientId: payload.clientId,
      absenceReasonType: payload.absenceReasonType,
    });

    yield put(actionCreators.GetMyClassificationsSchemaSuccess(schema));
  } catch (e) {
    yield put(actionCreators.GetMyClassificationsSchemaError(e.response));
  }
}

export function* onGetUserActiveShiftPattern({
  payload,
}: Action<MyAbsencesRequest>): Generator<*, void, Object> {
  if (!payload || !payload.clientId || !payload.employeeId) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const response = yield call(
      http.get,
      `/api/v1/user/self-service/client/${payload.clientId}/employee/${payload.employeeId}/shift-pattern/active`,
    );

    yield put(actionCreators.GetUserActiveShiftPatternSuccess(response.data));
  } catch (e) {
    // Normally the FE expects the status to be 200 with empty data (object, array)
    // this is workarround for the new BE response approach implemented into this endpoint
    // to return status 404 if no active shift patterns were not found
    if (e.response.status === 404) {
      yield put(actionCreators.GetUserActiveShiftPatternSuccess(null));
    } else {
      yield put(actionCreators.GetUserActiveShiftPatternError(e.response));
    }
  }
}

type MyAbsenceCreationRequest = $Exact<{
  ...MyAbsencesRequest,
  data: Object,
}>;
export function* onCreateMyAbsence({
  payload,
}: Action<MyAbsenceCreationRequest>): Generator<*, void, Object> {
  if (!payload || !payload.clientId || !payload.data) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const userId = yield select(getUserId);
    const data: Object = {
      ...payload.data,
      daysLost: payload.data.daysLost.map(e => ({
        recordedDay: e.recordedDay,
        workingTimeInSeconds: e.workingTimeInSeconds,
        timeLostInSeconds: e.timeLostInSeconds,
      })),
    };

    const response = yield call(
      http.post,
      `/api/v1/user/self-service/client/${payload.clientId}/absence`,
      data,
    );

    yield all([
      put(sendSuccessMessage("ABSENCE_CREATED")),
      put(actionCreators.CreateMyAbsenceSuccess(response.data)),
      put(actionCreators.GetMyAbsences({ clientId: payload.clientId, employeeId: userId })),
      put(
        actionCreators.GetMyStandardQuestions({
          clientId: payload.clientId,
          interactionId: response.data.interactionId,
        }),
      ),
      put(
        actionCreators.GetMyDynamicQuestions({
          clientId: payload.clientId,
          interactionId: response.data.interactionId,
        }),
      ),
      put(
        actionCreators.GetMyReferralQuestions({
          clientId: payload.clientId,
          interactionId: response.data.interactionId,
        }),
      ),
    ]);
  } catch (e) {
    yield put(actionCreators.CreateMyAbsenceError(e.response));
  }
}

export function* onSetAbsenceStatus({
  payload,
}: Action<{ clientId: number, absenceId: number, status: string }>): Generator<*, void, Object> {
  if (!payload || !payload.clientId || !payload.absenceId || !payload.status) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const userId = yield select(getUserId);
    const response = yield call(
      http.put,
      `/api/v1/user/self-service/client/${payload.clientId}/absence/${payload.absenceId}/status`,
      { status: payload.status },
    );

    yield all([
      put(sendSuccessMessage("ABSENCE_CLOSED")),
      put(actionCreators.SetAbsenceStatusSuccess(response.data)),
      put(actionCreators.SetShowAddMyAbsencesModal({ show: false })),
      put(actionCreators.SetShowCloseMyAbsencesModal(false)),
      put(actionCreators.SetShowReOpenMyAbsencesModal(false)),
      put(actionCreators.SetShowCancelMyAbsencesModal(false)),
      put(actionCreators.SetMyAbsenceDraft(null)),
      put(actionCreators.GetMyAbsences({ clientId: payload.clientId, employeeId: userId })),
    ]);
  } catch (e) {
    yield put(actionCreators.SetAbsenceStatusError(e.response));
  }
}

export function* onGetMyStandardQuestions({
  payload,
}: Action<{ clientId: number, interactionId: number }>): Generator<*, void, Object> {
  if (!payload || !payload.clientId || !payload.interactionId) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const response = yield call(
      http.get,
      `/api/v1/user/self-service/client/${payload.clientId}/interaction/${payload.interactionId}/standard-questions`,
    );

    yield put(actionCreators.GetMyStandardQuestionsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetMyStandardQuestionsError(e.response));
  }
}

export function* onAddMyStandardQuestions({
  payload,
}: Action<{ clientId: number, interactionId: number, data: Object }>): Generator<*, void, Object> {
  if (!payload || !payload.clientId || !payload.interactionId || !payload.data) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const response = yield call(
      http.post,
      `/api/v1/user/self-service/client/${payload.clientId}/interaction/${payload.interactionId}/standard-questions`,
      payload.data,
    );

    yield put(sendSuccessMessage("STANDARD_QUESTIONS_SUBMITED"));
    yield put(actionCreators.AddMyStandardQuestionsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.AddMyStandardQuestionsError(e.response));
  }
}

export function* onGetMyDynamicQuestions({
  payload,
}: Action<{ clientId: number, interactionId: number }>): Generator<*, void, Object> {
  if (!payload || !payload.clientId || !payload.interactionId) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const response = yield call(
      http.get,
      `/api/v1/user/self-service/client/${payload.clientId}/interaction/${payload.interactionId}/dynamic-questions`,
    );

    yield put(actionCreators.GetMyDynamicQuestionsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetMyDynamicQuestionsError(e.response));
  }
}

export function* onAddMyDynamicQuestions({
  payload,
}: Action<{ clientId: number, interactionId: number, data: Object }>): Generator<*, void, Object> {
  if (!payload || !payload.clientId || !payload.interactionId || !payload.data) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const response = yield call(
      http.post,
      `/api/v1/user/self-service/client/${payload.clientId}/interaction/${payload.interactionId}/dynamic-questions`,
      payload.data,
    );

    yield put(sendSuccessMessage("DYNAMIC_QUESTIONS_SUBMITED"));
    yield put(actionCreators.AddMyDynamicQuestionsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.AddMyDynamicQuestionsError(e.response));
  }
}

export function* onGetMyReferralQuestions({
  payload,
}: Action<{ clientId: number, interactionId: number }>): Generator<*, void, Object> {
  if (!payload || !payload.clientId || !payload.interactionId) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const response = yield call(
      http.get,
      `/api/v1/user/self-service/client/${payload.clientId}/interaction/${payload.interactionId}/employee-referrals`,
    );

    yield put(actionCreators.GetMyReferralQuestionsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetMyReferralQuestionsError(e.response));
  }
}

export function* onAddMyReferralQuestions({
  payload,
}: Action<{
  clientId: number,
  interactionId: number,
  data: Object,
  referralId: number,
}>): Generator<*, void, Object> {
  if (
    !payload ||
    !payload.clientId ||
    !payload.interactionId ||
    !payload.data ||
    !payload.referralId
  ) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const response = yield call(
      http.post,
      `/api/v1/user/self-service/client/${payload.clientId}/interaction/${payload.interactionId}/employee-referrals/${payload.referralId}`,
      payload.data,
    );

    yield put(sendSuccessMessage("REFERRAL_QUESTION_SUBMITED"));
    yield put(actionCreators.AddMyReferralQuestionsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.AddMyReferralQuestionsError(e.response));
  }
}

export function* onAddMyAdditionalInfoQuestion({
  payload,
}: Action<{
  clientId: number,
  interactionId: number,
  data: Object,
}>): Generator<*, void, Object> {
  if (!payload || !payload.clientId || !payload.interactionId || !payload.data) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const response = yield call(
      http.post,
      `/api/v1/user/self-service/client/${payload.clientId}/interaction/${payload.interactionId}/additional-info`,
      payload.data,
    );

    yield put(actionCreators.AddMyAdditionalInfoQuestionSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.AddMyAdditionalInfoQuestionError(e.response));
  }
}

export function* onExportMyAbsences({
  payload,
}: Action<{ clientId: number, employee: Object }>): Generator<*, void, Object> {
  if (!payload || !payload.clientId || !payload.employee) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const response = yield call(
      http.get,
      `/api/v1/user/self-service/client/${payload.clientId}/absence/export`,
    );

    yield put(
      actionCreators.ExportMyAbsencesSuccess({
        data: response.data,
        employee: payload.employee,
      }),
    );
  } catch (e) {
    yield put(actionCreators.ExportMyAbsencesError(e.response));
  }
}

export function* onExportMyAbsencesSuccess({
  payload,
}: Action<{
  data: Object,
  employee: Object,
}>): Generator<*, void, *> {
  if (!payload || !payload.data || !payload.employee) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  yield downloadCsvFile(
    payload.data,
    `${payload.employee.firstName}-${payload.employee.lastName}-my-absences.csv`,
  );
}

///////
//////////

type MyAbsenceGetRequest = $Exact<{
  data: ?Object,
  id: ?number,
}>;

export function* onGetMyAbsence({
  payload,
}: Action<MyAbsenceGetRequest>): Generator<*, void, Object> {
  if (!payload || !payload.id) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }
  try {
    const response = yield call(http.get, `/api/v1/my-details/my-absence/${payload.id}`);

    yield all([put(actionCreators.GetMyAbsenceSuccess(response.data))]);
  } catch (e) {
    yield put(actionCreators.GetMyAbsenceError(e.response));
  }
}

type MyAbsenceTimeLostGetRequest = $Exact<{
  clientId: number,
  absenceId: number,
}>;

export function* onGetMyAbsenceDaysLost({
  payload,
}: Action<MyAbsenceTimeLostGetRequest>): Generator<*, void, Object> {
  if (!payload || !payload.clientId || !payload.absenceId) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }
  try {
    const response = yield call(
      http.get,
      `api/v1/user/self-service/client/${payload.clientId}/absence/${payload.absenceId}/time-lost`,
    );

    yield put(actionCreators.GetMyAbsenceDaysLostSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetMyAbsenceDaysLostError(e.response));
  }
}

type UpdateMyAbsenceRequest = $Exact<{
  clientId: number,
  absenceId: number,
  data: Object,
}>;

export function* onUpdateMyAbsence({
  payload,
}: Action<UpdateMyAbsenceRequest>): Generator<*, void, Object> {
  if (!payload || !payload.clientId || !payload.absenceId) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  const userId = yield select(getUserId);

  try {
    const response = yield call(
      http.put,
      `api/v1/user/self-service/client/${payload.clientId}/absence/${payload.absenceId}`,
      {
        ...payload.data,
        // sending dates with a timezone included in order to calculate days lost in the BE
        // they convert the date to UTC and timezone is lost and set a wrong time
        startsAt: moment(payload.data?.startsAt).format(),
        rtwAt: moment(payload.data?.rtwAt).format(),
      },
    );

    yield all([
      put(sendSuccessMessage("ABSENCE_UPDATED")),
      put(actionCreators.UpdateMyAbsenceSuccess(response.data)),
      put(actionCreators.GetMyAbsences({ clientId: payload.clientId, employeeId: userId })),
      put(
        actionCreators.GetMyStandardQuestions({
          clientId: payload.clientId,
          interactionId: response.data.interactionId,
        }),
      ),
      put(
        actionCreators.GetMyDynamicQuestions({
          clientId: payload.clientId,
          interactionId: response.data.interactionId,
        }),
      ),
      put(
        actionCreators.GetMyReferralQuestions({
          clientId: payload.clientId,
          interactionId: response.data.interactionId,
        }),
      ),
    ]);
  } catch (e) {
    yield put(actionCreators.UpdateMyAbsenceError(e.response));
  }
}

export function* onGetCanOpenAbsence({
  payload,
}: Action<MyAbsenceTimeLostGetRequest>): Generator<*, void, Object> {
  if (!payload || !payload.clientId) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }
  const { clientId } = payload;
  try {
    const response = yield call(
      http.get,
      `api/v1/user/self-service/client/${clientId}/can-open-absence`,
    );

    yield put(actionCreators.GetCanOpenAbsenceSuccess(response.data));
  } catch (e) {
    yield put(toast.error("Something went wrong while checking if you are able to open absence"));
  }
}

export default function* MyAbsencesSaga(): Generator<*, void, void> {
  yield all([
    takeEvery(GetMyAbsences, onGetMyAbsences),
    takeEvery(GetMyAbsencesSchema, onGetMyAbsencesSchema),
    takeEvery(GetMyClassificationsSchema, onGetMyClassificationsSchema),
    takeEvery(GetUserActiveShiftPattern, onGetUserActiveShiftPattern),
    takeEvery(CreateMyAbsence, onCreateMyAbsence),
    // update absence action is missing
    takeEvery(SetAbsenceStatus, onSetAbsenceStatus),
    takeEvery(GetMyStandardQuestions, onGetMyStandardQuestions),
    takeEvery(AddMyStandardQuestions, onAddMyStandardQuestions),
    takeEvery(GetMyDynamicQuestions, onGetMyDynamicQuestions),
    takeEvery(AddMyDynamicQuestions, onAddMyDynamicQuestions),
    takeEvery(GetMyReferralQuestions, onGetMyReferralQuestions),
    takeEvery(AddMyReferralQuestions, onAddMyReferralQuestions),
    takeEvery(AddMyAdditionalInfoQuestion, onAddMyAdditionalInfoQuestion),
    takeEvery(GetMyAbsenceDaysLost, onGetMyAbsenceDaysLost),
    takeEvery(UpdateMyAbsence, onUpdateMyAbsence),
    takeEvery(GetMyAbsence, onGetMyAbsence),
    takeEvery(ExportMyAbsences, onExportMyAbsences),
    takeEvery(ExportMyAbsencesSuccess, onExportMyAbsencesSuccess),
    takeEvery(GetCanOpenAbsence, onGetCanOpenAbsence),
  ]);
}
